/**
 * SDDList Component
 *
 * Display a list of SDDs for a repository with status badges and actions
 */

import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  FileText,
  Plus,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  Clock,
  CheckCircle2,
  XCircle,
  Archive,
  Send,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { useSDDList, useDeleteSDD, type SDDResponse } from '@/hooks/useSDD';
import { useToast } from '@/shared/hooks/useToast';
import type { SDDDocumentStatus } from '@forgenetwork/types';

interface SDDListProps {
  repositoryId: string;
  onCreateNew?: () => void;
}

/**
 * Status configuration
 */
const statusConfig: Record<
  SDDDocumentStatus,
  { variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ComponentType<{ className?: string }>; label: string }
> = {
  draft: { variant: 'secondary', icon: Clock, label: 'Draft' },
  pending_review: { variant: 'outline', icon: Send, label: 'Pending Review' },
  approved: { variant: 'default', icon: CheckCircle2, label: 'Approved' },
  rejected: { variant: 'destructive', icon: XCircle, label: 'Rejected' },
  archived: { variant: 'outline', icon: Archive, label: 'Archived' },
};

/**
 * Format date for display
 */
function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format relative time
 */
function formatRelativeTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return formatDate(d);
}

/**
 * SDDList - Display and manage SDDs for a repository
 */
export function SDDList({ repositoryId, onCreateNew }: SDDListProps): React.JSX.Element {
  const { data, isLoading, error, refetch } = useSDDList(repositoryId);
  const deleteSDD = useDeleteSDD();
  const { toast } = useToast();

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [sddToDelete, setSddToDelete] = React.useState<SDDResponse | null>(null);

  const handleDeleteClick = (sdd: SDDResponse): void => {
    setSddToDelete(sdd);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!sddToDelete) return;

    try {
      await deleteSDD.mutateAsync({
        repoId: repositoryId,
        sddId: sddToDelete.id,
      });
      toast({
        title: 'SDD Deleted',
        description: `"${sddToDelete.title}" has been deleted.`,
      });
      setDeleteDialogOpen(false);
      setSddToDelete(null);
    } catch (err) {
      toast({
        title: 'Delete Failed',
        description: err instanceof Error ? err.message : 'Failed to delete SDD',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center gap-4 py-8">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <p className="text-destructive">
              {error instanceof Error ? error.message : 'Failed to load SDDs'}
            </p>
            <Button variant="outline" onClick={() => refetch()}>
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const sdds = data?.sdds ?? [];

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>System Design Documents</CardTitle>
              <CardDescription>
                {sdds.length === 0
                  ? 'No SDDs created yet'
                  : `${sdds.length} document${sdds.length !== 1 ? 's' : ''}`}
              </CardDescription>
            </div>
            {onCreateNew && (
              <Button onClick={onCreateNew}>
                <Plus className="h-4 w-4 mr-2" />
                New SDD
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {sdds.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No SDDs yet</h3>
              <p className="text-muted-foreground mb-4 max-w-sm">
                Create your first System Design Document to document your architecture and technical decisions.
              </p>
              {onCreateNew && (
                <Button onClick={onCreateNew}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create SDD
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {sdds.map((sdd) => {
                const statusInfo = statusConfig[sdd.status];
                const StatusIcon = statusInfo.icon;

                return (
                  <div
                    key={sdd.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        <FileText className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold truncate">{sdd.title}</h4>
                          <Badge variant="outline" className="text-xs">
                            v{sdd.version}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                          <Badge variant={statusInfo.variant} className="gap-1">
                            <StatusIcon className="h-3 w-3" />
                            {statusInfo.label}
                          </Badge>
                          <span>Updated {formatRelativeTime(sdd.updatedAt)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Link
                        to="/repositories/$repoId/sdd/$sddId"
                        params={{ repoId: repositoryId, sddId: sdd.id }}
                      >
                        <Button variant="ghost" size="sm">
                          <Eye className="h-4 w-4 mr-2" />
                          View
                        </Button>
                      </Link>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link
                              to="/repositories/$repoId/sdd/$sddId"
                              params={{ repoId: repositoryId, sddId: sdd.id }}
                              search={{ tab: 'view' }}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link
                              to="/repositories/$repoId/sdd/$sddId"
                              params={{ repoId: repositoryId, sddId: sdd.id }}
                              search={{ tab: 'edit' }}
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => handleDeleteClick(sdd)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete SDD</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{sddToDelete?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteSDD.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
